import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button, Col, Container, FloatingLabel, Form, Modal, Row, Spinner, Tab, Tabs } from 'react-bootstrap'
import { useLocation, useNavigate } from 'react-router-dom'
import Notification from '../components/Notification'
import StorageLabelsPrintTrigger from '../components/StorageLabelsPrintTrigger'
import StorageProtocolPrintTrigger from '../components/StorageProtocolPrintTrigger'
import {
  createUschovaPrijem,
  fetchUschovaDalsiCislo,
  fetchUschovaDetail,
  fetchUschovaNapoveda,
  issueUschovaZakazka,
  updateUschovaVozidlo,
  updateUschovaZakazka,
  updateUschovaZakaznik,
} from '../services/api/uschovy'

const toTrim = (v) => String(v ?? '').trim()
const toNum = (v, fallback = 0) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : fallback
}
const toBool = (v) => Number(v) === 1

const formatDate = (value) => {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = String(d.getFullYear())
  return `${dd}.${mm}.${yyyy}`
}
const formatStorageDays = (value) => {
  const n = Number(value)
  if (!Number.isFinite(n) || n < 0) return '-'
  if (n === 1) return '1 den'
  if (n >= 2 && n <= 4) return `${n} dny`
  return `${n} dnů`
}

const vehicleTypeLabel = (v) => {
  const n = Number(v)
  if (n === 1) return 'osobní'
  if (n === 2) return 'suv/4x4'
  if (n === 3) return 'dodávka'
  if (n === 4) return 'truck'
  if (n === 5) return 'moto'
  return ''
}

const seasonLabel = (v) => {
  const n = Number(v)
  if (n === 1) return 'letní'
  if (n === 2) return 'zimní'
  if (n === 3) return 'celoroční'
  return ''
}

const diskLabel = (v) => {
  const n = Number(v)
  if (n === 1) return 'ocel'
  if (n === 2) return 'hliník'
  return 'bez disku'
}

const smerLabel = (v) => {
  const s = String(v ?? '').trim().toLowerCase()
  if (s === '1' || s === 'prijem' || s === 'příjem') return 'přijato do úschovy'
  if (s === '2' || s === 'vydej' || s === 'výdej') return 'vydáno z úschovy'
  return 'změna v úschově'
}

const typVydejeLabel = (v) => {
  const s = String(v ?? '').trim().toLowerCase()
  if (s === '1' || s === 'montaz' || s === 'montáž') return 'montáž'
  if (s === '2' || s === 'kufr') return 'do kufru'
  if (s === '3' || s === 'likvidace') return 'likvidace'
  return '-'
}

const toDateInputValue = (value) => {
  if (!value) return ''
  const iso = String(value).trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso
  if (/^\d{4}-\d{2}-\d{2}\s/.test(iso)) return iso.slice(0, 10)
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

const emptyPneuRow = () => ({
  znacka: '',
  rozmer: '',
  model: '',
  hloubka: '',
  sezona: '1',
  disk: '0',
})

const templateFields = new Set(['znacka', 'rozmer', 'model', 'sezona', 'disk'])

const StorageZakazkaBlock = ({ zak, polozky, history = false, onEdit, onIssue }) => {
  const datumPrijmu = formatDate(zak?.datum_prijmu || zak?.datum)
  const datumVydeje = formatDate(zak?.datum_vydeje)
  const historyHeaderCols = [
    { value: toTrim(zak.cislo) || '-', label: 'zakázka' },
    { value: datumPrijmu || '-', label: 'datum příjmu' },
    { value: datumVydeje || '-', label: 'datum výdeje' },
    { value: formatStorageDays(zak.delka_uschovy_dny), label: 'délka úschovy' },
    { value: typVydejeLabel(zak.typ_vydeje), label: 'způsob výdeje' },
  ]
  return (
    <div className="bg-secondary-subtle rounded-3 p-4">
      <Row className="g-1 mb-2 align-items-center">
        <Col md={history ? 10 : 4}>
          {!history ? (
            <Row className="g-0 align-items-center">
              <Col xs={4} className="pe-3 border-end border-secondary-subtle">
                <div className="fw-semibold lh-1 ps-1">{toTrim(zak.cislo) || '-'}</div>
                <div className="small text-muted lh-1.2 ps-1">zakázka</div>
              </Col>
              <Col xs={8} className="px-3">
                <div className="fw-semibold lh-1">{datumPrijmu || '-'}</div>
                <div className="small text-muted lh-1.2">{smerLabel(zak.smer)}</div>
              </Col>
            </Row>
          ) : (
            <Row className="g-0 align-items-center">
              {historyHeaderCols.map((col, idx) => (
                <Col
                  key={`${zak.id_zakazky}-history-col-${idx}`}
                  xs={12}
                  md={idx === 0 ? 2 : 2}
                  className={`${idx > 0 ? 'ps-3 border-start border-secondary-subtle' : 'pe-3'}`}
                >
                  <div className="fw-semibold lh-1">{col.value}</div>
                  <div className="small text-muted lh-1.2">{col.label}</div>
                </Col>
              ))}
            </Row>
          )}
        </Col>
        <Col md={history ? 2 : 8} className="d-flex justify-content-md-end gap-1 pe-0">
          <Button
            className="p-2 opacity-75"
            size="sm"
            type="button"
            onClick={() => onEdit?.(zak)}
            variant="primary"
          >
            <i className="bi bi-pencil-square me-2"></i>Upravit
          </Button>
          {!history && (
            <>
              <StorageLabelsPrintTrigger
                zakazkaId={toNum(zak?.id_zakazky)}
                renderButton={({ onClick, disabled }) => (
                  <Button
                    className="p-2 opacity-75"
                    size="sm"
                    type="button"
                    variant="primary"
                    title="Tisk štítků"
                    onClick={onClick}
                    disabled={disabled}
                  >
                    <i className="bi bi-printer me-2"></i>Štítky
                  </Button>
                )}
              />
              <StorageProtocolPrintTrigger
                zakazkaId={toNum(zak?.id_zakazky)}
                renderButton={({ onClick, disabled }) => (
                  <Button
                    className="p-2 opacity-75"
                    size="sm"
                    type="button"
                    variant="primary"
                    title="Tisk protokolu"
                    onClick={onClick}
                    disabled={disabled}
                  >
                    <i className="bi bi-printer me-2"></i>Protokol
                  </Button>
                )}
              />
              <Button
                className="p-2 opacity-75 ms-3"
                size="sm"
                type="button"
                onClick={() => onIssue?.(zak, 'montaz')}
                variant="success"
              >
                <i className="bi bi-wrench me-2"></i>Montáž
              </Button>
              <Button
                className="p-2 opacity-75"
                size="sm"
                type="button"
                onClick={() => onIssue?.(zak, 'kufr')}
                variant="warning"
              >
                <i className="bi bi-bag me-2"></i>Do kufru
              </Button>
              <Button
                className="p-2 opacity-75"
                size="sm"
                type="button"
                onClick={() => onIssue?.(zak, 'likvidace')}
                variant="danger"
              >
                <i className="bi bi-trash3 me-2"></i>Likvidace
              </Button>
            </>
          )}
        </Col>
      </Row>

      <div className="d-grid gap-1">
        {polozky.map((p) => (
          <Row key={`${zak.id_zakazky}-${p.id_pneumatiky}`} className="g-1">
            <Col md={2}>
              <FloatingLabel label="značka">
                <Form.Control readOnly value={toTrim(p.znacka)} />
              </FloatingLabel>
            </Col>
            <Col md={3}>
              <FloatingLabel label="rozměr">
                <Form.Control readOnly value={toTrim(p.rozmer)} />
              </FloatingLabel>
            </Col>
            <Col md={2}>
              <FloatingLabel label="model">
                <Form.Control readOnly value={toTrim(p.model)} />
              </FloatingLabel>
            </Col>
            <Col md={1}>
              <FloatingLabel label="hloubka">
                <Form.Control readOnly value={toTrim(p.hloubka).replace('.', ',')} />
              </FloatingLabel>
            </Col>
            <Col md={2}>
              <FloatingLabel label="sezóna">
                <Form.Control readOnly value={seasonLabel(p.sezona)} />
              </FloatingLabel>
            </Col>
            <Col md={2}>
              <FloatingLabel label="disk">
                <Form.Control readOnly value={diskLabel(p.disk)} />
              </FloatingLabel>
            </Col>
          </Row>
        ))}
        <Row className="g-1">
          <Col md={5}>
            <FloatingLabel label="poznámka">
              <Form.Control readOnly value={toTrim(zak.poznamka)} />
            </FloatingLabel>
          </Col>
          <Col md={4}>
            <div className="h-100 d-flex align-items-center gap-3 px-2">
              <div className="small" title="TMPS">
                <i className={`bi ${toBool(zak.tmps) ? 'bi-check-square-fill text-primary' : 'bi-square text-muted'}`}></i>
                <span className="ms-1 text-muted">tmps</span>
              </div>
              <div className="small" title="Poklice">
                <i className={`bi ${toBool(zak.poklice) ? 'bi-check-square-fill text-primary' : 'bi-square text-muted'}`}></i>
                <span className="ms-1 text-muted">poklice</span>
              </div>
              <div className="small" title="Šrouby">
                <i className={`bi ${toBool(zak.srouby) ? 'bi-check-square-fill text-primary' : 'bi-square text-muted'}`}></i>
                <span className="ms-1 text-muted">šrouby</span>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  )
}

const StorageDetail = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const id = Number(location.state?.id) || 0

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [detail, setDetail] = useState({
    vehicle: null,
    customer: null,
    zakazky: [],
    pneumatikyByZakazka: [],
  })

  const [notifications, setNotifications] = useState([])

  const [showVehicleModal, setShowVehicleModal] = useState(false)
  const [validatedVehicle, setValidatedVehicle] = useState(false)
  const [isSavingVehicle, setIsSavingVehicle] = useState(false)
  const [vehicleSuggestions, setVehicleSuggestions] = useState({ znacka: [], model: [] })
  const vehicleSuggestTimersRef = useRef({})
  const [vehicleEdit, setVehicleEdit] = useState({
    spz: '',
    znacka: '',
    model: '',
    rok_vyroby: '',
    typ: '1',
    pocet_pneu: '',
    poznamka: '',
  })

  const [showCustomerModal, setShowCustomerModal] = useState(false)
  const [validatedCustomer, setValidatedCustomer] = useState(false)
  const [isSavingCustomer, setIsSavingCustomer] = useState(false)
  const [customerEdit, setCustomerEdit] = useState({
    firma: '',
    jmeno: '',
    ulice: '',
    psc: '',
    mesto: '',
    ico: '',
    email: '',
    telefon: '',
    poznamka: '',
  })

  const [showZakazkaModal, setShowZakazkaModal] = useState(false)
  const [zakazkaMode, setZakazkaMode] = useState('edit')
  const [validatedZakazka, setValidatedZakazka] = useState(false)
  const [isSavingZakazka, setIsSavingZakazka] = useState(false)
  const [pneuSuggestions, setPneuSuggestions] = useState({ znacka: [], rozmer: [], model: [] })
  const pneuSuggestTimersRef = useRef({})
  const [zakazkaEdit, setZakazkaEdit] = useState({
    id_zakazky: 0,
    cislo: '',
    datum: '',
    poznamka: '',
    tmps: false,
    poklice: false,
    srouby: false,
    polozky: [],
  })
  const [showIssueModal, setShowIssueModal] = useState(false)
  const [isIssuing, setIsIssuing] = useState(false)
  const [issueCtx, setIssueCtx] = useState({ id_zakazky: 0, cislo: '', typ: '' })

  useEffect(() => {
    return () => {
      Object.values(pneuSuggestTimersRef.current || {}).forEach((timerId) => {
        if (timerId) clearTimeout(timerId)
      })
      Object.values(vehicleSuggestTimersRef.current || {}).forEach((timerId) => {
        if (timerId) clearTimeout(timerId)
      })
    }
  }, [])

  const loadVehicleSuggestions = useCallback((field, value) => {
    const fieldMap = {
      znacka: 'vehicle_znacka',
      model: 'vehicle_model',
    }
    const apiField = fieldMap[field]
    if (!apiField) return

    const query = toTrim(value)
    if (query.length < 3) {
      setVehicleSuggestions((prev) => ({ ...prev, [field]: [] }))
      if (vehicleSuggestTimersRef.current[field]) clearTimeout(vehicleSuggestTimersRef.current[field])
      return
    }

    if (vehicleSuggestTimersRef.current[field]) clearTimeout(vehicleSuggestTimersRef.current[field])
    vehicleSuggestTimersRef.current[field] = setTimeout(async () => {
      try {
        const list = await fetchUschovaNapoveda(apiField, query)
        setVehicleSuggestions((prev) => ({
          ...prev,
          [field]: Array.isArray(list) ? list : [],
        }))
      } catch {
        setVehicleSuggestions((prev) => ({ ...prev, [field]: [] }))
      }
    }, 250)
  }, [])

  const loadPneuSuggestions = useCallback((field, value) => {
    const fieldMap = {
      znacka: 'pneu_znacka',
      rozmer: 'pneu_rozmer',
      model: 'pneu_model',
    }
    const apiField = fieldMap[field]
    if (!apiField) return

    const query = toTrim(value)
    if (query.length < 3) {
      setPneuSuggestions((prev) => ({ ...prev, [field]: [] }))
      if (pneuSuggestTimersRef.current[field]) clearTimeout(pneuSuggestTimersRef.current[field])
      return
    }

    if (pneuSuggestTimersRef.current[field]) clearTimeout(pneuSuggestTimersRef.current[field])
    pneuSuggestTimersRef.current[field] = setTimeout(async () => {
      try {
        const list = await fetchUschovaNapoveda(apiField, query)
        setPneuSuggestions((prev) => ({
          ...prev,
          [field]: Array.isArray(list) ? list : [],
        }))
      } catch {
        setPneuSuggestions((prev) => ({ ...prev, [field]: [] }))
      }
    }, 250)
  }, [])

  const loadDetail = useCallback(async () => {
    try {
      setIsLoading(true)
      setError('')
      setDetail({ vehicle: null, customer: null, zakazky: [], pneumatikyByZakazka: [] })

      if (id <= 0) {
        setError('Chybí identifikace záznamu.')
        return
      }

      const data = await fetchUschovaDetail(id)
      setDetail({
        vehicle: data?.vehicle || null,
        customer: data?.customer || null,
        zakazky: Array.isArray(data?.zakazky) ? data.zakazky : [],
        pneumatikyByZakazka: Array.isArray(data?.pneumatikyByZakazka) ? data.pneumatikyByZakazka : [],
      })
    } catch (e) {
      setError(e?.message || 'Nepodařilo se načíst detail úschovy.')
    } finally {
      setIsLoading(false)
    }
  }, [id])

  useEffect(() => {
    loadDetail()
  }, [loadDetail])

  const vehicleForm = useMemo(
    () => ({
      spz: toTrim(detail.vehicle?.spz),
      znacka: toTrim(detail.vehicle?.znacka),
      model: toTrim(detail.vehicle?.model),
      rok_vyroby: toTrim(detail.vehicle?.rok_vyroby),
      typ: vehicleTypeLabel(detail.vehicle?.typ),
      pocet_pneu: toTrim(detail.vehicle?.pocet_pneu),
      poznamka: toTrim(detail.vehicle?.vozidlo_poznamka),
    }),
    [detail.vehicle]
  )

  const customerForm = useMemo(
    () => ({
      firma: toTrim(detail.customer?.firma),
      jmeno: toTrim(detail.customer?.jmeno),
      ulice: toTrim(detail.customer?.ulice),
      psc: toTrim(detail.customer?.psc),
      mesto: toTrim(detail.customer?.obec),
      ico: toTrim(detail.customer?.ico),
      email: toTrim(detail.customer?.email),
      telefon: toTrim(detail.customer?.telefon),
      poznamka: toTrim(detail.customer?.zakaznik_poznamka || detail.customer?.poznamka),
    }),
    [detail.customer]
  )

  const pneuMapByZakazka = useMemo(() => {
    const map = new Map()
    ;(detail.pneumatikyByZakazka || []).forEach((z) => {
      map.set(toNum(z?.id_zakazky), Array.isArray(z?.polozky) ? z.polozky : [])
    })
    return map
  }, [detail.pneumatikyByZakazka])

  const hasPneuRows = useCallback(
    (idZakazky) => (pneuMapByZakazka.get(toNum(idZakazky)) || []).length > 0,
    [pneuMapByZakazka]
  )

  const uskladneneZakazky = useMemo(
    () => (detail.zakazky || []).filter((z) => hasPneuRows(z.id_zakazky) && toNum(z.stav, -1) === 1),
    [detail.zakazky, hasPneuRows]
  )

  const historieZakazky = useMemo(
    () => (detail.zakazky || []).filter((z) => hasPneuRows(z.id_zakazky) && toNum(z.stav, -1) !== 1),
    [detail.zakazky, hasPneuRows]
  )

  const openVehicleModal = () => {
    setValidatedVehicle(false)
    setVehicleSuggestions({ znacka: [], model: [] })
    setVehicleEdit({
      spz: toTrim(detail.vehicle?.spz),
      znacka: toTrim(detail.vehicle?.znacka),
      model: toTrim(detail.vehicle?.model),
      rok_vyroby: toTrim(detail.vehicle?.rok_vyroby),
      typ: String(toNum(detail.vehicle?.typ, 1)),
      pocet_pneu: toTrim(detail.vehicle?.pocet_pneu),
      poznamka: toTrim(detail.vehicle?.vozidlo_poznamka),
    })
    setShowVehicleModal(true)
  }

  const openCustomerModal = () => {
    setValidatedCustomer(false)
    setCustomerEdit({
      firma: toTrim(detail.customer?.firma),
      jmeno: toTrim(detail.customer?.jmeno),
      ulice: toTrim(detail.customer?.ulice),
      psc: toTrim(detail.customer?.psc),
      mesto: toTrim(detail.customer?.obec),
      ico: toTrim(detail.customer?.ico),
      email: toTrim(detail.customer?.email),
      telefon: toTrim(detail.customer?.telefon),
      poznamka: toTrim(detail.customer?.zakaznik_poznamka || detail.customer?.poznamka),
    })
    setShowCustomerModal(true)
  }

  const handleVehicleEditChange = (e) => {
    const { name, value } = e.target
    if (name === 'znacka' || name === 'model') {
      loadVehicleSuggestions(name, value)
    }
    setVehicleEdit((prev) => ({ ...prev, [name]: value }))
  }

  const handleCustomerEditChange = (e) => {
    const { name, value } = e.target
    setCustomerEdit((prev) => ({ ...prev, [name]: value }))
  }

  const openZakazkaModal = (zak) => {
    const rows = (pneuMapByZakazka.get(toNum(zak?.id_zakazky)) || []).map((p) => ({
      znacka: toTrim(p.znacka),
      rozmer: toTrim(p.rozmer),
      model: toTrim(p.model),
      hloubka: toTrim(p.hloubka).replace('.', ','),
      sezona: String(toNum(p.sezona, 1)),
      disk: String(toNum(p.disk, 0)),
    }))

    setValidatedZakazka(false)
    setZakazkaMode('edit')
    setZakazkaEdit({
      id_zakazky: toNum(zak?.id_zakazky),
      cislo: toTrim(zak?.cislo),
      datum: toDateInputValue(zak?.datum_prijmu || zak?.datum),
      poznamka: toTrim(zak?.poznamka),
      tmps: toBool(zak?.tmps),
      poklice: toBool(zak?.poklice),
      srouby: toBool(zak?.srouby),
      polozky: rows.length ? rows : [emptyPneuRow()],
    })
    setPneuSuggestions({ znacka: [], rozmer: [], model: [] })
    setShowZakazkaModal(true)
  }

  const openPrijemModal = async () => {
    const today = new Date()
    const yyyy = today.getFullYear()
    const mm = String(today.getMonth() + 1).padStart(2, '0')
    const dd = String(today.getDate()).padStart(2, '0')
    const datum = `${yyyy}-${mm}-${dd}`

    const anchorZak = (detail.zakazky || []).find((z) => toNum(z?.id_zakazky) === toNum(id))
    const isFirstIntakeOnAnchor = !!anchorZak && !hasPneuRows(anchorZak.id_zakazky)

    let cislo = ''
    let editIdZakazky = 0
    if (isFirstIntakeOnAnchor) {
      cislo = toTrim(anchorZak?.cislo)
      editIdZakazky = toNum(anchorZak?.id_zakazky)
    } else {
      try {
        const res = await fetchUschovaDalsiCislo(datum)
        cislo = toTrim(res?.cislo)
      } catch {
        cislo = ''
      }
    }

    let rows = []
    const historyTemplateZak = (detail.zakazky || []).find((z) => {
      if (toNum(z?.stav, 1) === 1) return false
      const src = pneuMapByZakazka.get(toNum(z?.id_zakazky)) || []
      return src.length > 0
    })

    if (historyTemplateZak) {
      const src = pneuMapByZakazka.get(toNum(historyTemplateZak.id_zakazky)) || []
      rows = src.map((p) => ({
        znacka: toTrim(p.znacka),
        rozmer: toTrim(p.rozmer),
        model: toTrim(p.model),
        hloubka: '',
        sezona: String(toNum(p.sezona, 1)),
        disk: String(toNum(p.disk, 0)),
      }))
    } else {
      const pneuCountRaw = toNum(detail.vehicle?.pocet_pneu, 4)
      const pneuCount = Math.min(Math.max(pneuCountRaw, 1), 12)
      rows = Array.from({ length: pneuCount }, () => emptyPneuRow())
    }

    setValidatedZakazka(false)
    setZakazkaMode('create')
    setZakazkaEdit({
      id_zakazky: editIdZakazky,
      cislo,
      datum,
      poznamka: '',
      tmps: false,
      poklice: false,
      srouby: false,
      polozky: rows,
    })
    setPneuSuggestions({ znacka: [], rozmer: [], model: [] })
    setShowZakazkaModal(true)
  }

  const handleZakazkaChange = (e) => {
    const { name, value, type, checked } = e.target
    setZakazkaEdit((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
  }

  const handleZakazkaPneuChange = (index, field, value) => {
    if (field === 'znacka' || field === 'rozmer' || field === 'model') {
      loadPneuSuggestions(field, value)
    }
    setZakazkaEdit((prev) => {
      const next = [...prev.polozky]
      const old = next[index] || emptyPneuRow()
      const oldValue = old[field]
      next[index] = { ...old, [field]: value }

      if (zakazkaMode === 'create' && index === 0 && templateFields.has(field)) {
        for (let i = 1; i < next.length; i += 1) {
          const row = next[i] || emptyPneuRow()
          const shouldPropagate = toTrim(row[field]) === '' || String(row[field]) === String(oldValue ?? '')
          if (shouldPropagate) next[i] = { ...row, [field]: value }
        }
      }

      return { ...prev, polozky: next }
    })
  }

  const addZakazkaPneuRow = () => {
    setZakazkaEdit((prev) => {
      const last = prev.polozky?.[prev.polozky.length - 1]
      const nextRow = last
        ? {
            znacka: toTrim(last.znacka),
            rozmer: toTrim(last.rozmer),
            model: toTrim(last.model),
            hloubka: '',
            sezona: String(toNum(last.sezona, 1)),
            disk: String(toNum(last.disk, 0)),
          }
        : emptyPneuRow()
      return { ...prev, polozky: [...prev.polozky, nextRow] }
    })
  }

  const removeZakazkaPneuRow = (index) => {
    setZakazkaEdit((prev) => {
      const rows = Array.isArray(prev.polozky) ? [...prev.polozky] : []
      if (rows.length <= 1) return { ...prev, polozky: [emptyPneuRow()] }
      rows.splice(index, 1)
      return { ...prev, polozky: rows.length ? rows : [emptyPneuRow()] }
    })
  }

  const handleSaveVehicle = async (e) => {
    e.preventDefault()
    const form = e.currentTarget
    if (form && !form.checkValidity()) {
      setValidatedVehicle(true)
      return
    }

    try {
      setIsSavingVehicle(true)
      const idVozidla = toNum(detail.vehicle?.id_vozidla)
      if (idVozidla <= 0) throw new Error('Nepodařilo se určit vozidlo pro uložení.')

      await updateUschovaVozidlo(idVozidla, {
        spz: toTrim(vehicleEdit.spz).replace(/\s+/g, '').toUpperCase(),
        znacka: toTrim(vehicleEdit.znacka),
        model: toTrim(vehicleEdit.model),
        rok_vyroby: toTrim(vehicleEdit.rok_vyroby),
        typ: toNum(vehicleEdit.typ),
        pocet_pneu: toNum(vehicleEdit.pocet_pneu),
        poznamka: toTrim(vehicleEdit.poznamka),
      })

      setShowVehicleModal(false)
      setValidatedVehicle(false)
      setNotifications((prev) => [...prev, { id: Date.now(), type: 'success', message: 'Vozidlo bylo upraveno.' }])
      await loadDetail()
    } catch (err) {
      setNotifications((prev) => [...prev, { id: Date.now(), type: 'danger', message: err?.message || 'Uložení vozidla se nepodařilo.' }])
    } finally {
      setIsSavingVehicle(false)
    }
  }

  const handleSaveCustomer = async (e) => {
    e.preventDefault()
    const form = e.currentTarget
    if (form && !form.checkValidity()) {
      setValidatedCustomer(true)
      return
    }

    try {
      setIsSavingCustomer(true)
      const idZakaznika = toNum(detail.customer?.id_zakaznika)
      if (idZakaznika <= 0) throw new Error('Nepodařilo se určit zákazníka pro uložení.')

      await updateUschovaZakaznik(idZakaznika, {
        firma: toTrim(customerEdit.firma),
        jmeno: toTrim(customerEdit.jmeno),
        ulice: toTrim(customerEdit.ulice),
        psc: toTrim(customerEdit.psc),
        obec: toTrim(customerEdit.mesto),
        ico: toTrim(customerEdit.ico),
        email: toTrim(customerEdit.email),
        telefon: toTrim(customerEdit.telefon),
        poznamka: toTrim(customerEdit.poznamka),
      })

      setShowCustomerModal(false)
      setValidatedCustomer(false)
      setNotifications((prev) => [...prev, { id: Date.now(), type: 'success', message: 'Zákazník byl upraven.' }])
      await loadDetail()
    } catch (err) {
      setNotifications((prev) => [...prev, { id: Date.now(), type: 'danger', message: err?.message || 'Uložení zákazníka se nepodařilo.' }])
    } finally {
      setIsSavingCustomer(false)
    }
  }

  const handleSaveZakazka = async (e) => {
    e.preventDefault()
    const form = e.currentTarget
    if (form && !form.checkValidity()) {
      setValidatedZakazka(true)
      return
    }

    try {
      setIsSavingZakazka(true)
      const payload = {
        datum: toTrim(zakazkaEdit.datum),
        poznamka: toTrim(zakazkaEdit.poznamka),
        tmps: zakazkaEdit.tmps ? 1 : 0,
        poklice: zakazkaEdit.poklice ? 1 : 0,
        srouby: zakazkaEdit.srouby ? 1 : 0,
        polozky: (zakazkaEdit.polozky || []).map((p) => ({
          znacka: toTrim(p.znacka),
          rozmer: toTrim(p.rozmer),
          model: toTrim(p.model),
          hloubka: toTrim(p.hloubka).replace(',', '.'),
          sezona: toNum(p.sezona, 1),
          disk: toNum(p.disk, 0),
        })),
      }

      if (zakazkaMode === 'create') {
        if (toNum(zakazkaEdit.id_zakazky) > 0) {
          await updateUschovaZakazka(toNum(zakazkaEdit.id_zakazky), payload)
        } else {
          if (id <= 0) throw new Error('Nepodařilo se určit kartu vozidla pro příjem.')
          await createUschovaPrijem({ id_anchor: id, ...payload })
        }
      } else {
        const idZakazky = toNum(zakazkaEdit.id_zakazky)
        if (idZakazky <= 0) throw new Error('Nepodařilo se určit zakázku pro uložení.')
        await updateUschovaZakazka(idZakazky, payload)
      }

      setShowZakazkaModal(false)
      setValidatedZakazka(false)
      setNotifications((prev) => [
        ...prev,
        {
          id: Date.now(),
          type: 'success',
          message: zakazkaMode === 'create' ? 'Nová sada pneumatik byla přijata do úschovy.' : 'Zakázka byla upravena.',
        },
      ])
      await loadDetail()
    } catch (err) {
      setNotifications((prev) => [...prev, { id: Date.now(), type: 'danger', message: err?.message || 'Uložení zakázky se nepodařilo.' }])
    } finally {
      setIsSavingZakazka(false)
    }
  }

  const openIssueModal = (zak, typ) => {
    setIssueCtx({
      id_zakazky: toNum(zak?.id_zakazky),
      cislo: toTrim(zak?.cislo),
      typ: String(typ || '').toLowerCase(),
    })
    setShowIssueModal(true)
  }

  const issueLabel = (typ) => {
    if (typ === 'montaz') return 'MONTÁŽ'
    if (typ === 'kufr') return 'DO KUFRU'
    if (typ === 'likvidace') return 'LIKVIDACE'
    return ''
  }

  const handleConfirmIssue = async () => {
    try {
      setIsIssuing(true)
      const idZakazky = toNum(issueCtx.id_zakazky)
      if (idZakazky <= 0) throw new Error('Nepodařilo se určit zakázku pro výdej.')

      await issueUschovaZakazka(idZakazky, issueCtx.typ)
      setShowIssueModal(false)
      setNotifications((prev) => [
        ...prev,
        { id: Date.now(), type: 'success', message: `Zakázka ${issueCtx.cislo || ''} byla vydána ze skladu.`.trim() },
      ])
      await loadDetail()
    } catch (err) {
      setNotifications((prev) => [
        ...prev,
        { id: Date.now(), type: 'danger', message: err?.message || 'Výdej ze skladu se nepodařil.' },
      ])
    } finally {
      setIsIssuing(false)
    }
  }

  const goToStorages = () => navigate('/storages')

  return (
    <div className="d-grid gap-2">
      {isLoading && (
        <div className="text-muted small d-flex align-items-center">
          <Spinner animation="border" size="sm" className="me-2" />
          Načítám detail karty...
        </div>
      )}

      {!!error && (
        <Alert variant="danger" className="mb-0 small">
          {error}
        </Alert>
      )}

      {!isLoading && !error && (
        <Row className="g-3 justify-content-start">
          <Col xxl={4} xl={5} lg={6} md={6} sm={6}>
            <div>
              <div className="d-flex align-items-center mb-2">
                <div className="fs-5 d-flex align-items-center gap-2">
                  <i className="bi bi-car-front-fill"></i>
                  <span>Vozidlo</span>
                </div>
              </div>
              <ul className="list-unstyled small d-grid gap-1 mb-0">
                <li>
                  <span className="text-muted me-2">SPZ:</span>
                  <span className="fs-6 fw-semibold">
                    {vehicleForm.spz || '-'}
                    <Button
                      variant="link"
                      title="Upravit vozidlo"
                      type="button"
                      onClick={openVehicleModal}
                      className="ms-3 p-0 border-0 text-primary text-decoration-none shadow-none"
                    >
                      <i className="bi bi-pencil-square"></i>
                    </Button>
                  </span>
                </li>
                <li>
                  <span className="text-muted me-2">Značka / model:</span>
                  <span>{`${vehicleForm.znacka || '-'} ${vehicleForm.model || ''}`.trim()}</span>
                </li>
                <li><span className="text-muted me-2">Rok výroby:</span><span>{vehicleForm.rok_vyroby || '-'}</span></li>
                <li>
                  <span className="text-muted me-2">Typ / počet pneu:</span>
                  <span>{`${vehicleForm.typ || '-'} / ${vehicleForm.pocet_pneu || '-'}`}</span>
                </li>
                <li><span className="text-muted me-2">Poznámka:</span><span>{vehicleForm.poznamka || '-'}</span></li>
              </ul>
            </div>
          </Col>

          <Col xxl={4} xl={5} lg={6}  md={6} sm={6}>
            <div>
              <div className="d-flex align-items-center mb-2">
                <div className="fs-5 d-flex align-items-center gap-2">
                  <i className="bi bi-person-fill"></i>
                  <span>Zákazník</span>
                </div>
              </div>
              <ul className="list-unstyled small d-grid gap-1 mb-0">
                <li>
                  <span className="text-muted me-2">Firma / IČ:</span>
                  <span className="fs-6 fw-semibold">
                    {`${customerForm.firma || '-'}${customerForm.ico ? ` / ${customerForm.ico}` : ''}`}
                    <Button
                      variant="link"
                      title="Upravit zákazníka"
                      type="button"
                      onClick={openCustomerModal}
                      className="ms-3 p-0 border-0 text-primary text-decoration-none shadow-none"
                    >
                      <i className="bi bi-pencil-square"></i>
                    </Button>
                  </span>
                </li>
                <li><span className="text-muted me-2">Jméno:</span><span>{customerForm.jmeno || '-'}</span></li>
                <li>
                  <span className="text-muted me-2">Adresa:</span>
                  <span>{`${customerForm.ulice || ''}, ${customerForm.psc || ''} ${customerForm.mesto || ''}`.replace(/^,\s*/, '').trim() || '-'}</span>
                </li>
                <li><span className="text-muted me-2">E-mail / telefon:</span><span>{`${customerForm.email || '-'} / ${customerForm.telefon || '-'}`}</span></li>
                <li><span className="text-muted me-2">Poznámka:</span><span>{customerForm.poznamka || '-'}</span></li>
              </ul>
            </div>
          </Col>

          <Col xxl={9} xl={11} lg={12}>
            <div className="mt-3">
              <div className="fs-5 d-flex align-items-center gap-2">
                <i className="bi bi-box2-fill"></i>
                <div className="fs-5">Úschovy</div>
              </div>
              <Tabs defaultActiveKey="uskladnene" id="storage-zakazky-tabs" className="my-2">
                <Tab eventKey="uskladnene" title="Uskladněné">
                  <div className="d-grid gap-2">
                    {uskladneneZakazky.map((zak) => (
                      <StorageZakazkaBlock
                        key={`u-${zak.id_zakazky}`}
                        zak={zak}
                        polozky={pneuMapByZakazka.get(toNum(zak.id_zakazky)) || []}
                        onEdit={openZakazkaModal}
                        onIssue={openIssueModal}
                      />
                    ))}
                    {!uskladneneZakazky.length && (
                      <Alert variant="light" className="d-flex align-items-center small py-3 px-3 mb-0">
                        <i className="bi bi-info-circle me-2"></i>
                        <span>Aktuálně nejsou uskladněné žádné pneumatiky.</span>
                      </Alert>
                    )}
                    <div className="pt-2">
                      <Button className="p-2 px-3" type="button" variant="primary" onClick={openPrijemModal}>
                        <i className="bi bi-plus-circle me-2"></i>Příjem do úschovy
                      </Button>
                    </div>
                  </div>
                </Tab>

                <Tab eventKey="historie" title="Historie">
                  <div className="d-grid gap-2">
                    {historieZakazky.map((zak) => (
                      <StorageZakazkaBlock
                        key={`h-${zak.id_zakazky}`}
                        zak={zak}
                        polozky={pneuMapByZakazka.get(toNum(zak.id_zakazky)) || []}
                        history
                        onEdit={openZakazkaModal}
                        onIssue={openIssueModal}
                      />
                    ))}
                    {!historieZakazky.length && (
                      <Alert variant="light" className="d-flex align-items-center small py-3 px-3 mb-0">
                        <i className="bi bi-info-circle me-2"></i>
                        <span>Historie je zatím prázdná.</span>
                      </Alert>
                    )}
                  </div>
                </Tab>
              </Tabs>
            </div>
          </Col>
        </Row>
      )}

      {!isLoading && !error && (
        <div className="d-flex gap-1 mt-5">
          <Button type="button" variant="secondary" className="px-3 py-2" onClick={goToStorages}>
            <i className="bi bi-arrow-left me-2"></i>
            Zpět na přehled
          </Button>
        </div>
      )}

      <div className="d-none" data-zakazky={detail.zakazky.length} data-pneu={detail.pneumatikyByZakazka.length}></div>

      <Modal show={showVehicleModal} fullscreen="xl-down" onHide={() => setShowVehicleModal(false)} keyboard={true} centered size="md">
        <Modal.Header closeButton className="border-0 p-4">
          <Modal.Title className="text-primary lh-1">
            Vozidlo<br />
            <small className="text-muted fs-6">úprava stávajícího záznamu</small>
          </Modal.Title>
        </Modal.Header>
        <Form noValidate validated={validatedVehicle} onSubmit={handleSaveVehicle}>
          <Modal.Body className="pt-0">
            <Container fluid className="px-1">
              <Row className="g-2">
                <Col xs={12}>
                  <FloatingLabel label={<>spz <span className="text-danger">*</span></>}>
                    <Form.Control
                      required
                      name="spz"
                      value={vehicleEdit.spz}
                      onChange={handleVehicleEditChange}
                      minLength={6}
                      title="Zadejte minimálně 7 znaků."
                      onBlur={(e) =>
                        setVehicleEdit((p) => ({ ...p, spz: toTrim(e.target.value).replace(/\s+/g, '').toUpperCase() }))
                      }
                    />
                  </FloatingLabel>
                </Col>
                <Col xs={6}>
                  <FloatingLabel label="značka">
                    <Form.Control
                      name="znacka"
                      list="storage-vehicle-znacka-list"
                      value={vehicleEdit.znacka}
                      onChange={handleVehicleEditChange}
                    />
                  </FloatingLabel>
                </Col>
                <Col xs={6}>
                  <FloatingLabel label="model">
                    <Form.Control
                      name="model"
                      list="storage-vehicle-model-list"
                      value={vehicleEdit.model}
                      onChange={handleVehicleEditChange}
                    />
                  </FloatingLabel>
                </Col>
                <Col xs={3}>
                  <FloatingLabel label="rok výroby">
                    <Form.Control
                      name="rok_vyroby"
                      value={vehicleEdit.rok_vyroby}
                      onChange={handleVehicleEditChange}
                      pattern="^[1-9][0-9]{3}$"
                      title="Uveďte rok ve formátu rrrr."
                      inputMode="numeric"
                    />
                  </FloatingLabel>
                </Col>
                <Col xs={6}>
                  <FloatingLabel label={<>typ <span className="text-danger">*</span></>}>
                    <Form.Select required name="typ" value={vehicleEdit.typ} onChange={handleVehicleEditChange}>
                      <option value="1">osobní</option>
                      <option value="2">suv/4x4</option>
                      <option value="3">dodávka</option>
                      <option value="4">truck</option>
                      <option value="5">moto</option>
                    </Form.Select>
                  </FloatingLabel>
                </Col>
                <Col xs={3}>
                  <FloatingLabel label={<>počet pneu <span className="text-danger">*</span></>}>
                    <Form.Control
                      required
                      name="pocet_pneu"
                      value={vehicleEdit.pocet_pneu}
                      onChange={handleVehicleEditChange}
                      pattern="^[1-9]$"
                      title="Povoleny jsou čísla od 1 do 9."
                      inputMode="numeric"
                    />
                  </FloatingLabel>
                </Col>
                <Col xs={12}>
                  <FloatingLabel label="poznámka">
                    <Form.Control as="textarea" rows={3} name="poznamka" value={vehicleEdit.poznamka} onChange={handleVehicleEditChange} />
                  </FloatingLabel>
                </Col>
              </Row>
              <datalist id="storage-vehicle-znacka-list">
                {(vehicleSuggestions.znacka || []).map((value, i) => (
                  <option key={`storage-vehicle-znacka-${i}-${value}`} value={value} />
                ))}
              </datalist>
              <datalist id="storage-vehicle-model-list">
                {(vehicleSuggestions.model || []).map((value, i) => (
                  <option key={`storage-vehicle-model-${i}-${value}`} value={value} />
                ))}
              </datalist>
            </Container>
          </Modal.Body>
          <Modal.Footer className="border-0 d-flex justify-content-between px-3 pb-3">
            <div>
              <Button className="me-1" variant="primary" type="submit" disabled={isSavingVehicle}>
                {isSavingVehicle ? 'Ukládám...' : 'Uložit'}
              </Button>
              <Button variant="secondary" onClick={() => setShowVehicleModal(false)}>
                Zavřít
              </Button>
            </div>
            <p className='text-muted small mt-2 mb-0'><span className="text-danger me-1">*</span>označení povinných polí</p>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showCustomerModal} fullscreen="xl-down" onHide={() => setShowCustomerModal(false)} keyboard={true} centered size="md">
        <Modal.Header closeButton className="border-0 p-4">
          <Modal.Title className="text-primary lh-1">
            Zákazník<br />
            <small className="text-muted fs-6">úprava stávajícího záznamu</small>
          </Modal.Title>
        </Modal.Header>
        <Form noValidate validated={validatedCustomer} onSubmit={handleSaveCustomer}>
          <Modal.Body className="pt-0">
            <Container fluid className="px-1">
              <Row className="g-2">
                <Col xs={12}>
                  <FloatingLabel label="firma">
                    <Form.Control name="firma" value={customerEdit.firma} onChange={handleCustomerEditChange} />
                  </FloatingLabel>
                </Col>
                <Col xs={12}>
                  <FloatingLabel label={<>jméno <span className="text-danger">*</span></>}>
                    <Form.Control required name="jmeno" value={customerEdit.jmeno} onChange={handleCustomerEditChange} />
                  </FloatingLabel>
                </Col>
                <Col xs={12}>
                  <FloatingLabel label="ulice">
                    <Form.Control name="ulice" value={customerEdit.ulice} onChange={handleCustomerEditChange} />
                  </FloatingLabel>
                </Col>
                <Col xs={4}>
                  <FloatingLabel label="psč">
                    <Form.Control
                      name="psc"
                      value={customerEdit.psc}
                      onChange={handleCustomerEditChange}
                      pattern="^$|^[0-9]{5}$"
                      title="Pokud je PSČ vyplněno, zadejte přesně 5 číslic."
                      inputMode="numeric"
                      onBlur={(e) => setCustomerEdit((p) => ({ ...p, psc: toTrim(e.target.value).replace(/\s+/g, '') }))}
                    />
                  </FloatingLabel>
                </Col>
                <Col xs={8}>
                  <FloatingLabel label="město">
                    <Form.Control name="mesto" value={customerEdit.mesto} onChange={handleCustomerEditChange} />
                  </FloatingLabel>
                </Col>
                <Col xs={12}>
                  <FloatingLabel label="ič">
                    <Form.Control name="ico" value={customerEdit.ico} onChange={handleCustomerEditChange} />
                  </FloatingLabel>
                </Col>
                <Col xs={12}>
                  <FloatingLabel label="e-mail">
                    <Form.Control name="email" value={customerEdit.email} onChange={handleCustomerEditChange} />
                  </FloatingLabel>
                </Col>
                <Col xs={12}>
                  <FloatingLabel label="telefon">
                    <Form.Control name="telefon" value={customerEdit.telefon} onChange={handleCustomerEditChange} />
                  </FloatingLabel>
                </Col>
                <Col xs={12}>
                  <FloatingLabel label="poznámka">
                    <Form.Control as="textarea" rows={3} name="poznamka" value={customerEdit.poznamka} onChange={handleCustomerEditChange} />
                  </FloatingLabel>
                </Col>
              </Row>
            </Container>
          </Modal.Body>
          <Modal.Footer className="border-0 d-flex justify-content-between px-3 pb-3">
            <div>
              <Button className="me-1" variant="primary" type="submit" disabled={isSavingCustomer}>
                {isSavingCustomer ? 'Ukládám...' : 'Uložit'}
              </Button>
              <Button variant="secondary" onClick={() => setShowCustomerModal(false)}>
                Zavřít
              </Button>
            </div>
             <p className='text-muted small mt-2 mb-0'><span className="text-danger me-1">*</span>označení povinných polí</p>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showZakazkaModal} fullscreen="xl-down" onHide={() => setShowZakazkaModal(false)} keyboard={true} centered size="xl">
        <Modal.Header closeButton className="border-0 p-4">
          <Modal.Title className="text-primary lh-1">
            Úschova
            <br />
            <small className="text-muted fs-6">
              {zakazkaMode === 'create' ? `příjem nové sady` : `úprava stávajícího záznamu`}
            </small>
          </Modal.Title>
        </Modal.Header>
        <Form noValidate validated={validatedZakazka} onSubmit={handleSaveZakazka}>
          <Modal.Body className="pt-0">
            <Container fluid className="px-1">
              <Row className="g-1 mb-1">
                <Col md={2}>
                  <FloatingLabel label="číslo zakázky">
                    <Form.Control value={zakazkaEdit.cislo} disabled />
                  </FloatingLabel>
                </Col>
                <Col md={2}>
                  <FloatingLabel label={<>datum <span className="text-danger">*</span></>}>
                    <Form.Control
                      required
                      type="date"
                      name="datum"
                      value={zakazkaEdit.datum}
                      onChange={handleZakazkaChange}
                    />
                  </FloatingLabel>
                </Col>
              </Row>
              <div className="d-grid gap-1">
                {zakazkaEdit.polozky.map((p, idx) => (
                  <Row className="g-1" key={`zp-${idx}`}>
                    <Col md={2}>
                      <FloatingLabel label={<>značka <span className="text-danger">*</span></>}>
                        <Form.Control
                          required
                          list="storage-pneu-znacka-list"
                          value={p.znacka}
                          onChange={(e) => handleZakazkaPneuChange(idx, 'znacka', e.target.value)}
                        />
                      </FloatingLabel>
                    </Col>
                    <Col md={2}>
                      <FloatingLabel label={<>rozměr <span className="text-danger">*</span></>}>
                        <Form.Control
                          required
                          list="storage-pneu-rozmer-list"
                          value={p.rozmer}
                          onChange={(e) => handleZakazkaPneuChange(idx, 'rozmer', e.target.value)}
                        />
                      </FloatingLabel>
                    </Col>
                    <Col md={2}>
                      <FloatingLabel label={<>model <span className="text-danger">*</span></>}>
                        <Form.Control
                          required
                          list="storage-pneu-model-list"
                          value={p.model}
                          onChange={(e) => handleZakazkaPneuChange(idx, 'model', e.target.value)}
                        />
                      </FloatingLabel>
                    </Col>
                    <Col md={1}>
                      <FloatingLabel label={<>hloubka <span className="text-danger">*</span></>}>
                        <Form.Control
                          required
                          value={p.hloubka}
                          pattern="^[0-9]+,[0-9]$"
                          title="Zadejte hloubku ve formátu 6,1."
                          inputMode="decimal"
                          onChange={(e) => handleZakazkaPneuChange(idx, 'hloubka', e.target.value)}
                          onBlur={(e) =>
                            handleZakazkaPneuChange(idx, 'hloubka', toTrim(e.target.value).replace(/\s+/g, '').replace('.', ','))
                          }
                        />
                      </FloatingLabel>
                    </Col>
                    <Col md={2}>
                      <FloatingLabel label={<>sezóna <span className="text-danger">*</span></>}>
                        <Form.Select
                          required
                          value={p.sezona}
                          onChange={(e) => handleZakazkaPneuChange(idx, 'sezona', e.target.value)}
                        >
                          <option value="1">letní</option>
                          <option value="2">zimní</option>
                          <option value="3">celoroční</option>
                        </Form.Select>
                      </FloatingLabel>
                    </Col>
                    <Col md={2}>
                      <FloatingLabel label={<>disk <span className="text-danger">*</span></>}>
                        <Form.Select
                          required
                          value={p.disk}
                          onChange={(e) => handleZakazkaPneuChange(idx, 'disk', e.target.value)}
                        >
                          <option value="0">bez</option>
                          <option value="1">ocel</option>
                          <option value="2">hliník</option>
                        </Form.Select>
                      </FloatingLabel>
                    </Col>
                    <Col md={1} className="d-flex align-items-center">
                      <Button
                        type="button"
                        className='p-3'
                        variant="outline-secondary"
                        size="sm"
                        title="Smazat řádek"
                        onClick={() => removeZakazkaPneuRow(idx)}
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </Col>
                  </Row>
                ))}
              </div>

              <Row className="g-1 mt-1">
                
                <Col md={5}>
                  <FloatingLabel label="poznámka">
                    <Form.Control
                      name="poznamka"
                      value={zakazkaEdit.poznamka}
                      onChange={handleZakazkaChange}
                    />
                  </FloatingLabel>
                </Col>
                <Col md={4}>
                  <div className="h-100 d-flex align-items-center gap-3 px-3">
                    <Form.Check
                      id="zakazka-tmps"
                      type="checkbox"
                      label="tmps"
                      name="tmps"
                      checked={zakazkaEdit.tmps}
                      onChange={handleZakazkaChange}
                    />
                    <Form.Check
                      id="zakazka-poklice"
                      type="checkbox"
                      label="poklice"
                      name="poklice"
                      checked={zakazkaEdit.poklice}
                      onChange={handleZakazkaChange}
                    />
                    <Form.Check
                      id="zakazka-srouby"
                      type="checkbox"
                      label="šrouby"
                      name="srouby"
                      checked={zakazkaEdit.srouby}
                      onChange={handleZakazkaChange}
                    />
                  </div>
                </Col>
              </Row>

              <div className="mt-2">
                <Button type="button" variant="outline-primary" className="px-3" onClick={addZakazkaPneuRow}>
                  <i className="bi bi-plus-circle me-2"></i>Pneu
                </Button>
              </div>
              <datalist id="storage-pneu-znacka-list">
                {(pneuSuggestions.znacka || []).map((value, i) => (
                  <option key={`storage-znacka-${i}-${value}`} value={value} />
                ))}
              </datalist>
              <datalist id="storage-pneu-rozmer-list">
                {(pneuSuggestions.rozmer || []).map((value, i) => (
                  <option key={`storage-rozmer-${i}-${value}`} value={value} />
                ))}
              </datalist>
              <datalist id="storage-pneu-model-list">
                {(pneuSuggestions.model || []).map((value, i) => (
                  <option key={`storage-model-${i}-${value}`} value={value} />
                ))}
              </datalist>
            </Container>
          </Modal.Body>
          <Modal.Footer className="border-0 d-flex justify-content-between px-3 pb-3">
            <div>
              <Button className="me-1" variant="primary" type="submit" disabled={isSavingZakazka}>
                {isSavingZakazka ? 'Ukládám...' : 'Uložit'}
              </Button>
              <Button variant="secondary" onClick={() => setShowZakazkaModal(false)}>
                Zavřít
              </Button>
            </div>
            <p className="text-muted small mt-2 mb-0">
              <span className="text-danger me-1">*</span>označení povinných polí
            </p>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showIssueModal} fullscreen="xl-down" onHide={() => setShowIssueModal(false)} keyboard={true} centered size="md">
        <Modal.Header closeButton className="border-0 px-4 pt-4">
          <Modal.Title className="text-primary lh-1">
            Úschova
            <br />
            <small className="text-muted fs-6">výdej ze skladu</small>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4">
          <Alert variant='warning' className="d-flex align-items-center my-2">
            <i className="bi bi-exclamation-triangle pe-3 fs-3"></i>
            <span>
            Chcete skutečně vydat vybrané pneu z úschovy číslo <strong>{issueCtx.cislo}</strong>? Zvolený způsob výdeje <strong>{issueLabel(issueCtx.typ)}</strong>. Zakázka bude přesunuta do historie.
            </span>
          </Alert>
          <div className="mt-5">
            <Button className='me-1' variant="primary" onClick={handleConfirmIssue} disabled={isIssuing}>
            {isIssuing ? 'Vydávám...' : 'Vydat'}
            </Button>
            <Button className='mx-0' variant="secondary" onClick={() => setShowIssueModal(false)} disabled={isIssuing}>
              Zavřít
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <Notification notifications={notifications} setNotifications={setNotifications} />
    </div>
  )
}

export default StorageDetail


